import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { Importer } from '@feathr/blackbox';
import { Text } from '@feathr/components';
import {
  formatImportCount,
  getPartialImportCounts,
} from '@feathr/extender/components/ImportRunLines';

interface IPartialImportLineProps {
  importer: Importer;
}

/** How many of the rows it read a run imported, when it did not import them all. */
function PartialImportLine({ importer }: Readonly<IPartialImportLineProps>): JSX.Element | null {
  const { t } = useTranslation();

  const counts = getPartialImportCounts(importer);
  if (!counts) {
    return null;
  }

  const imported = t('We imported {{imported}} of {{formattedTotal}} rows into {{- name}}.', {
    count: counts.total,
    formattedTotal: formatImportCount(counts.total),
    imported: formatImportCount(counts.imported),
    name: importer.name,
  });
  const notImported = t('The other {{formattedCount}} were not imported.', {
    count: counts.skipped,
    formattedCount: formatImportCount(counts.skipped),
  });

  // Placeholder copy, pending Product and Design.
  return (
    <Text.DefaultRegular>
      {counts.skipped > 0 ? `${imported} ${notImported}` : imported}
    </Text.DefaultRegular>
  );
}

export default observer(PartialImportLine);
